import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { PeopleTwoIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getPcrPayScale } from '../../api/pcr-pay-scale/get-pcr-pay-scale'
import { updatePcrPayScale } from '../../api/pcr-pay-scale/update-pcr-pay-scales'
import { ItemNotFound } from '../../components/elements/item-not-found'
import UserNavBar from '../../components/elements/nav-bar'
import { PcrPayScaleForm } from '../../components/pages/pcr-pay-scale/pcr-pay-scale-form'
import { Paths } from '../../lib/constants/path'
import { PcrPayScaleInput } from '../../lib/types/models/pcr-pay-scales/pcr-pay-scale-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const PcrPayScaleUpdatePage = () => {
  const { id: pcrPayScaleId } = useParams()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  if (!pcrPayScaleId || isNaN(parseInt(pcrPayScaleId)))
    return <ItemNotFound title={'Identifiant de barème invalide'} />

  const { data, isLoading } = useQuery({
    queryKey: ['pcr-pay-scales', pcrPayScaleId],
    queryFn: () => getPcrPayScale(parseInt(pcrPayScaleId)),
    enabled: !!pcrPayScaleId
  })

  const { mutateAsync } = useMutation({
    mutationFn: updatePcrPayScale,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pcr-pay-scales'] })
      toast.success('Barème de rémunération modifié avec succès')
    }
  })

  const onSubmit = async (values: PcrPayScaleInput) => {
    await mutateAsync({ id: parseInt(pcrPayScaleId), ...values })
    queryClient.invalidateQueries({ queryKey: ['pcr-pay-scales'] })
  }

  if (isLoading) return <Loader />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier un barème de rémunération'} icon={PeopleTwoIcon} />
      <div className="h-4" />
      <Box>
        <div className="flex justify-between items-center py-2">
          <div className="flex items-center gap-4">
            <Typography variant="sub-heading">Barème de rémunération</Typography>
          </div>
        </div>
        <div className="h-5" />
        <PcrPayScaleForm
          defaultValues={data}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.PCR_PAY_SCALES),
            onUpdate: onSubmit,
            onUpdateThenCreate: async (values) => {
              await onSubmit(values)
              navigate(Paths.PCR_PAY_SCALE_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await onSubmit(values)
              navigate(Paths.PCR_PAY_SCALES, { replace: true })
            }
          })}
        />
      </Box>
    </div>
  )
}

export default PcrPayScaleUpdatePage
